import { VehicleParking } from "@1games-ru/region-shared/dist/enums/vehicle/vehicle";
import {
  VehicleDetailsDto,
  VehicleRelatedDto,
} from "@1games-ru/region-shared/dist/types/vehicle/vehicle-b";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import VehicleMpProvider from "@/core/data-provider/data-mp/vehicle/vehicle-mp-provider";
import useAppSelector from "@/core/helpers/useAppSelector";
import { RpcMpProviderFailedResponse } from "@/core/rpc-mp-provider/responses/rpc-mp-provider-failed-response";
import { RpcMpProviderSuccessResponse } from "@/core/rpc-mp-provider/responses/rpc-mp-provider-success-response.ts";
import { estateSelector } from "@/domains/estate/slice";
import toast from "@/domains/toasts/helpers/toast";

export default () => {
  const { t } = useTranslation("estate");
  const estate = useAppSelector(estateSelector);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [detaching, setDetaching] = useState(false);
  const [vehicleDetail, setVehicleDetail] = useState<VehicleDetailsDto | null>(null);
  const [vehiclesRelated, setVehiclesRelated] = useState<VehicleRelatedDto[]>([]);
  const [vehicles, setVehicles] = useState<VehicleRelatedDto[]>([]);

  const loadVehicles = useCallback(async () => {
    setLoading(true);
    const response = await VehicleMpProvider.getVehiclesEstate(estate.id);

    if (response instanceof RpcMpProviderFailedResponse) {
      console.error(response);
      toast.error(t("estate.errors.ERROR_LOAD_VEHICLES"));
    } else {
      setVehicles(response.data);
    }

    setLoading(false);
  }, [t, estate.id]);

  const loadVehicleDetail = useCallback(
    async (vehicleId: number) => {
      setLoading(true);
      const response = await VehicleMpProvider.getVehicleEstate(vehicleId);

      if (response instanceof RpcMpProviderFailedResponse) {
        console.error(response);
        toast.error(t("estate.errors.ERROR_LOAD_VEHICLE_DETAIL"));
      } else {
        setVehicleDetail(response.data);
      }

      setLoading(false);
    },
    [t],
  );

  const loadVehiclesRelated = useCallback(async () => {
    setLoading(true);
    const response = await VehicleMpProvider.getVehiclesRelated();

    if (response instanceof RpcMpProviderFailedResponse) {
      console.error(response);
      toast.error(t("estate.errors.ERROR_LOAD_VEHICLES_RELATED"));
    } else {
      setVehiclesRelated(response.data);
    }

    setLoading(false);
  }, [t]);

  const parkingUpdate = useCallback(
    async (vehicle: VehicleRelatedDto, index: number) => {
      if (!estate) {
        return;
      }

      setUpdating(true);
      const response = await VehicleMpProvider.parkingUpdate({
        id: estate.id,
        type: VehicleParking.Estate,
        vehicleId: vehicle.vehicleId,
        index,
      });
      if (response instanceof RpcMpProviderFailedResponse) {
        console.error("estate menu vehicles reset parking fail", response);
        toast.error(t("estate.vehicles.errors.UPDATE_PARKING_ERROR"));
      } else {
        toast.success(
          t("estate.vehicles.successUpdateParking", {
            brand: vehicle.name.brandName,
            model: vehicle.name.modelName,
            address: estate.address,
            type: t(`estate.vehicles.estateType.estate`),
          }),
        );
      }

      setUpdating(false);
    },
    [estate, t],
  );

  const parkingReset = useCallback(
    async (vehicleId: number) => {
      setResetting(true);
      const response = await VehicleMpProvider.parkingReset(vehicleId);

      if (response instanceof RpcMpProviderSuccessResponse) {
        toast.success(t("estate.vehicles.successResetParking"));
      } else {
        console.error("estate menu vehicles reset parking fail", response);
        toast.error(t("estate.vehicles.errors.RESET_PARKING_ERROR"));
      }

      setResetting(false);
    },
    [t],
  );

  const characterDetach = useCallback(
    async (vehicleId: number, characterId: number) => {
      setDetaching(true);
      const response = await VehicleMpProvider.characterDetach(vehicleId, characterId);
      if (response instanceof RpcMpProviderSuccessResponse) {
        toast.success(t("estate.vehicles.successCharacterDetach"));
      } else {
        console.error("estate menu vehicle character detach fail", response);
        toast.error(t(`estate.vehicles.errors.${response.error.message}`));
      }
      setDetaching(false);
    },
    [t],
  );

  return {
    vehicles,
    loadVehicles,
    vehiclesRelated,
    loadVehiclesRelated,
    loading,
    updating,
    parkingUpdate,
    resetting,
    parkingReset,
    vehicleDetail,
    loadVehicleDetail,
    setVehicleDetail,
    detaching,
    characterDetach,
  };
};
